import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, Chip, Divider,
  Card, CardContent, CircularProgress,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import {
  Close, LocalHospital, Science, Medication, MonitorHeart,
  CameraAlt, Notes, ExpandMore, HotelRounded, AccessTime,
  Assignment, Print, Person,
} from "@mui/icons-material";
import axios from "axios";
import {
  Document      as DocxDocument,
  Packer,
  Paragraph     as DocxParagraph,
  TextRun,
  Table         as DocxTable,
  TableRow      as DocxTableRow,
  TableCell     as DocxTableCell,
  AlignmentType,
  BorderStyle,
  WidthType,
  ShadingType,
} from "docx";
import {
  getPatientVisitDetailsdb,
  visitVitalsdb,
  visitNurseNotesdb,
  visitIPDDiagnosisdb,
  visitIPDAdmissiondb,
} from "../BaseURL";


// ─────────────────────────────────────────────────────────────────────
// NAV SECTIONS
// ─────────────────────────────────────────────────────────────────────
const buildSections = (isIPD) => [
  ...(!isIPD ? [{ key: "opd",  label: "Doctor's Notes",    icon: <Notes sx={{ fontSize: 20 }} />,        color: "#0D9488" }] : []),
  ...(isIPD  ? [{ key: "ipd",  label: "Admission Records", icon: <HotelRounded sx={{ fontSize: 20 }} />, color: "#BE185D" }] : []),
  { key: "vitals",      label: "Vital Signs",    icon: <MonitorHeart sx={{ fontSize: 20 }} />, color: "#DC2626" },
  { key: "lab",         label: "Laboratory",     icon: <Science sx={{ fontSize: 20 }} />,      color: "#7C3AED" },
  { key: "scan",        label: "Scan / Imaging", icon: <CameraAlt sx={{ fontSize: 20 }} />,    color: "#2563EB" },
  { key: "medications", label: "Medications",    icon: <Medication sx={{ fontSize: 20 }} />,   color: "#D97706" },
  { key: "nurse",       label: "Nurse Notes",    icon: <Assignment sx={{ fontSize: 20 }} />,   color: "#059669" },
];


// ─────────────────────────────────────────────────────────────────────
// SHARED: Doctor badge
// ─────────────────────────────────────────────────────────────────────
const DoctorBadge = ({ name, label = "Attending Physician", color = "#0D9488", bg = "#F0FDFA", border = "#CCFBF1" }) => {
  if (!name || String(name).trim() === "" || String(name).trim() === "null" || String(name).trim() === "-") return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 2, pt: 1.5, borderTop: "1px dashed #E2E8F0" }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: bg, border: `1px solid ${border}`,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Person sx={{ fontSize: 15, color }} />
      </Box>
      <Box>
        <Typography variant="caption" color="#94A3B8" fontWeight={600}
          textTransform="uppercase" letterSpacing={0.5} sx={{ display: "block", lineHeight: 1 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} color={color}>. {name}</Typography>
      </Box>
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────
const EmptySection = ({ message }) => (
  <Box sx={{ textAlign: "center", py: 8 }}>
    <Typography variant="body1" color="#94A3B8">{message}</Typography>
  </Box>
);

const InfoRow = ({ label, value, color = "#334155" }) => {
  if (!value || String(value).trim() === "" || String(value).trim() === "null") return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="#64748B" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
        {label}
      </Typography>
      <Typography variant="body2" color={color} sx={{ mt: 0.25, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        {value}
      </Typography>
    </Box>
  );
};

// Date-only comparison helper
const sameDay = (a, b) => {
  if (!a || !b) return false;
  return String(a).substring(0, 10) === String(b).substring(0, 10);
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: DOCTOR'S NOTES (OPD)
// ─────────────────────────────────────────────────────────────────────
const OpdNotesSection = ({ details }) => {
  if (!details) return <EmptySection message="No doctor's notes found for this visit." />;

  const textSections = [
    { title: "Patient Complaint",    content: details.complaint,          icon: "💬", color: "#ef4444" },
    { title: "History",              content: details.history,            icon: "📋", color: "#f59e0b" },
    { title: "Physical Examination", content: details.physicalExam,       icon: "🩺", color: "#3b82f6" },
    { title: "Other Documentation",  content: details.otherDocumentation, icon: "📄", color: "#8b5cf6" },
    { title: "Diagnosis",            content: details.diagnosis,          icon: "🔬", color: "#10b981" },
    { title: "Procedure",            content: details.Procedure_,         icon: "⚕️", color: "#06b6d4" },
  ].filter((s) => s.content && String(s.content).trim() !== "");

  if (textSections.length === 0) return <EmptySection message="No doctor's notes found for this visit." />;

  return (
    <Box>
      {textSections.map((section, idx) => (
        <Card key={idx} elevation={0}
          sx={{ mb: 2, border: "1px solid #E2E8F0", borderRadius: 2,
            "&:hover": { boxShadow: `0 4px 16px ${section.color}22`, borderColor: section.color, transition: "all 0.2s" } }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: `${section.color}15`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>
                {section.icon}
              </Box>
              <Typography variant="subtitle2" fontWeight={700} color="#0F172A">{section.title}</Typography>
            </Box>
            <Typography variant="body2" color="#475569" sx={{ lineHeight: 1.7, pl: 6 }}>
              {section.content}
            </Typography>
            {idx === textSections.length - 1 && (
              <Box sx={{ pl: 6 }}>
                <DoctorBadge name={details.doctor} />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: VITALS
// ─────────────────────────────────────────────────────────────────────
const VitalsSection = ({ vitalsRows }) => {
  if (!vitalsRows || vitalsRows.length === 0)
    return <EmptySection message="No vitals recorded for this visit." />;

  const vitalDefs = [
    { label: "Temperature",      key: "tem",   unit: "°C",   color: "#f59e0b" },
    { label: "Pulse",            key: "pulse", unit: "bpm",  color: "#ec4899" },
    { label: "Blood Pressure",   key: "pre",   unit: "mmHg", color: "#ef4444" },
    { label: "Respiratory Rate", key: "res",   unit: "/min", color: "#06b6d4" },
    { label: "Weight",           key: "we",    unit: "kg",   color: "#8b5cf6" },
    { label: "Height",           key: "high",  unit: "cm",   color: "#10b981" },
    { label: "SpO₂",             key: "spo2",  unit: "%",    color: "#3b82f6" },
    { label: "BMI",              key: "bmi",   unit: "",     color: "#6366f1" },
  ];

  return (
    <Box>
      {vitalsRows.map((row, rIdx) => (
        <Card key={rIdx} elevation={0} sx={{ mb: 2, border: "1px solid #E2E8F0", borderRadius: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <AccessTime sx={{ fontSize: 16, color: "#64748B" }} />
              <Typography variant="caption" fontWeight={600} color="#64748B">
                {row.dat_vist}{row.time ? ` • ${row.time}` : ""}
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {vitalDefs.map((def) => {
                  const val = row[def.key];
                  if (!val || String(val).trim() === "") return null;
                  return (
                    <TableRow key={def.key}
                      sx={{ "&:last-child td": { border: 0 }, "&:hover": { bgcolor: "action.hover" } }}>
                      <TableCell sx={{ border: 0, py: 1, width: "45%", display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: def.color, flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={600} color="#475569">{def.label}</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 0, py: 1 }}>
                        <Box component="span" sx={{ color: def.color, fontWeight: 700, fontSize: "0.95rem" }}>{val}</Box>
                        {def.unit && <Box component="span" sx={{ color: "#94A3B8", ml: 0.5, fontSize: "0.8rem" }}>{def.unit}</Box>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <DoctorBadge name={row.doctor} label="Recorded By" color="#DC2626" bg="#FEF2F2" border="#FECACA" />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: IPD
// admissionDate = visitRow.dat (the date the patient was admitted)
// Diagnosis rows on that exact date → pre-admission OPD note
// Diagnosis rows on any other date → ward round notes (shown under Admission Information)
// ─────────────────────────────────────────────────────────────────────
const IpdSection = ({ diagnosisRows, admissionRows, admissionDate }) => {

  const diagFields = [
    { label: "Patient Complaint",    key: "symptorms"   },
    { label: "Examination Result",   key: "ex_result"   },
    { label: "Patient History",      key: "His_Comp"    },
    { label: "Physical Examination", key: "Other_doc"   },
    { label: "Diagnosis",            key: "documenting" },
    { label: "Procedure",            key: "Procedure_"  },
  ];

  const admFields = [
    { label: "Patient Complaint",      key: "symptorms"             },
    { label: "Examination Result",     key: "ex_result"             },
    { label: "Physical Examination",   key: "Other_doc"             },
    { label: "Surgical Notes",         key: "Surgical_Notes"        },
    { label: "Doctor's Plan",          key: "plan"                  },
    { label: "Note Time",              key: "note_time"             },
    { label: "Discharge Note",         key: "discharge_note"        },
    { label: "Diagnosis",              key: "documenting"           },
    { label: "Procedure",              key: "Procedure_"            },
    { label: "Nurse Discharge Reason", key: "Nuse_discharge_reason" },
    { label: "Nurse Discharge Notes",  key: "Nuse_discharge_notes"  },
    { label: "Discharge Date/Time",    key: "Nuse_discharge_date"   },
  ];

  // ── Split: admission-date records = pre-admission OPD note
  //          all other dates         = ward round notes
  const preAdmissionRows = (diagnosisRows || []).filter(row => sameDay(row.dat, admissionDate));
  const wardDiagRows     = (diagnosisRows || []).filter(row => !sameDay(row.dat, admissionDate));

  const hasAdm = admissionRows && admissionRows.length > 0;

  if (!diagnosisRows?.length && !hasAdm)
    return <EmptySection message="No inpatient records found for this admission." />;

  // Shared accordion for a diag-table row
  const renderDiagAccordion = (row, idx, keyPrefix, defaultExpanded = false) => {
    const hasContent = diagFields.some(f => row[f.key] && String(row[f.key]).trim() !== "");
    if (!hasContent) return null;
    return (
      <Accordion key={`${keyPrefix}-${row.trans_code || idx}`} defaultExpanded={defaultExpanded} elevation={0}
        sx={{ mb: 1.5, border: "1px solid #E2E8F0", borderRadius: "8px !important", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "#FDF2F8", borderRadius: "8px", minHeight: 48 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTime sx={{ fontSize: 15, color: "#BE185D" }} />
            <Typography variant="body2" fontWeight={600} color="#BE185D">{row.dat || "—"}</Typography>
            {row.documenting && (
              <Chip label={row.documenting.slice(0, 30) + (row.documenting.length > 30 ? "…" : "")}
                size="small" sx={{ fontSize: "0.7rem", bgcolor: "#FCE7F3", color: "#9D174D", ml: 1 }} />
            )}
            {row.doctor && (
              <Chip icon={<Person sx={{ fontSize: "13px !important" }} />}
                label={`. ${row.doctor}`} size="small"
                sx={{ fontSize: "0.7rem", bgcolor: "#FDF4FF", color: "#7C3AED", ml: "auto" }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.5 }}>
          {diagFields.map(f => {
            const val = row[f.key];
            if (!val || String(val).trim() === "" || String(val).trim() === "null") return null;
            return (
              <Box key={f.key}>
                <InfoRow label={f.label} value={val} />
                <Divider sx={{ mt: 1, mb: 1.5 }} />
              </Box>
            );
          })}
          <DoctorBadge name={row.doctor} color="#BE185D" bg="#FDF2F8" border="#FBCFE8" />
        </AccordionDetails>
      </Accordion>
    );
  };

  const normalizeState = (value) => String(value || "").trim().toLowerCase();

  const renderAdmissionAccordion = (row, idx, prefix = "adm") => {
    const visibleFields = admFields.filter(f => {
      const val = row[f.key];
      return val && String(val).trim() !== "" && String(val).trim() !== "null";
    });
    if (visibleFields.length === 0) return null;
    return (
      <Accordion key={`${prefix}-${row.trans_code || idx}`} defaultExpanded={idx === 0} elevation={0}
        sx={{ mb: 1.5, border: "1px solid #E2E8F0", borderRadius: "8px !important", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "#F5F3FF", borderRadius: "8px", minHeight: 48 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
            <AccessTime sx={{ fontSize: 15, color: "#7C3AED" }} />
            <Typography variant="body2" fontWeight={600} color="#7C3AED">{row.dat || "—"}</Typography>
            {row.note_time && <Typography variant="caption" color="#94A3B8">@ {row.note_time}</Typography>}
            {row.state && (
              <Chip label={row.state} size="small" sx={{ fontSize: "0.7rem",
                bgcolor: normalizeState(row.state) === "discharged" ? "#D1FAE5" : "#EDE9FE",
                color:  normalizeState(row.state) === "discharged" ? "#065F46" : "#5B21B6" }} />
            )}
            {row.doctor && (
              <Chip icon={<Person sx={{ fontSize: "13px !important" }} />}
                label={`. ${row.doctor}`} size="small"
                sx={{ fontSize: "0.7rem", bgcolor: "#EDE9FE", color: "#5B21B6", ml: "auto" }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.5 }}>
          {visibleFields.map(f => (
            <Box key={f.key}>
              <InfoRow label={f.label} value={row[f.key]}
                color={f.key === "documenting" ? "#059669" : f.key === "Nuse_discharge_date" ? "#BE185D" : "#334155"} />
              <Divider sx={{ mt: 1, mb: 1.5 }} />
            </Box>
          ))}
          <DoctorBadge name={row.doctor} label="Documented By" color="#7C3AED" bg="#F5F3FF" border="#DDD6FE" />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderDischargeAccordion = (row, idx) => {
    const dischargeFields = [
      { label: "Patient Complaint",      key: "symptorms"             },
      { label: "Examination Result",     key: "ex_result"             },
      { label: "Discharge Note",         key: "discharge_note"        },
      { label: "Diagnosis",              key: "documenting"           },
      { label: "Procedure",              key: "Procedure_"            },
      { label: "Nurse Discharge Reason", key: "Nuse_discharge_reason" },
      { label: "Nurse Discharge Notes",  key: "Nuse_discharge_notes"  },
      { label: "Discharge Date/Time",    key: "Nuse_discharge_date"   },
    ].filter(f => { const val = row[f.key]; return val && String(val).trim() !== "" && String(val).trim() !== "null"; });
    if (dischargeFields.length === 0) return null;
    return (
      <Accordion key={`dis-${row.trans_code || idx}`} defaultExpanded={idx === 0} elevation={0}
        sx={{ mb: 1.5, border: "1px solid #D1FAE5", borderRadius: "8px !important", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "#ECFDF5", borderRadius: "8px", minHeight: 48 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
            <AccessTime sx={{ fontSize: 15, color: "#059669" }} />
            <Typography variant="body2" fontWeight={700} color="#059669">{row.dat || "—"}</Typography>
            {row.note_time && <Typography variant="caption" color="#94A3B8">@ {row.note_time}</Typography>}
            <Chip label="Discharged" size="small" sx={{ fontSize: "0.7rem", bgcolor: "#D1FAE5", color: "#065F46" }} />
            {row.doctor && (
              <Chip icon={<Person sx={{ fontSize: "13px !important" }} />}
                label={`. ${row.doctor}`} size="small"
                sx={{ fontSize: "0.7rem", bgcolor: "#DCFCE7", color: "#166534", ml: "auto" }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.5 }}>
          {dischargeFields.map(f => (
            <Box key={f.key}>
              <InfoRow label={f.label} value={row[f.key]}
                color={f.key === "Nuse_discharge_date" ? "#BE185D" : "#334155"} />
              <Divider sx={{ mt: 1, mb: 1.5 }} />
            </Box>
          ))}
          <DoctorBadge name={row.doctor} label="Discharged By" color="#059669" bg="#ECFDF5" border="#A7F3D0" />
        </AccordionDetails>
      </Accordion>
    );
  };

  const admissionOnlyRows = (admissionRows || []).filter(row => normalizeState(row.state) !== "discharged");
  const dischargedRows    = (admissionRows || []).filter(row => normalizeState(row.state) === "discharged");
  const showAdmissionSection = hasAdm || wardDiagRows.length > 0;

  return (
    <Box>

      {/* ── Pre-admission OPD Consultation Note (admission date only) ── */}
      {preAdmissionRows.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: "#BE185D" }} />
            <Typography variant="subtitle1" fontWeight={700} color="#0F172A">
              Pre-Admission Outpatient Consultation Note
            </Typography>
            <Chip label={admissionDate} size="small"
              sx={{ fontSize: "0.7rem", bgcolor: "#FCE7F3", color: "#9D174D", ml: 1 }} />
          </Box>
          {preAdmissionRows.map((row, idx) => renderDiagAccordion(row, idx, "pre", idx === 0))}
        </Box>
      )}

      {/* ── Admission Information ── */}
      {showAdmissionSection && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: "#7C3AED" }} />
            <Typography variant="subtitle1" fontWeight={700} color="#0F172A">Admission Information</Typography>
          </Box>

          {/* Ward notes from diagnosis table on non-admission dates */}
          {wardDiagRows.length > 0 && (
            <Box sx={{ mb: admissionOnlyRows.length > 0 || dischargedRows.length > 0 ? 2 : 0 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#BE185D" sx={{ mb: 1.5 }}>
                Ward Notes
              </Typography>
              {wardDiagRows.map((row, idx) => renderDiagAccordion(row, idx, "ward", false))}
            </Box>
          )}

          {/* Admission / progress rows */}
          {admissionOnlyRows.length > 0 && (
            <Box sx={{ mb: dischargedRows.length > 0 ? 3 : 0 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#7C3AED" sx={{ mb: 1.5 }}>
                Admission / Progress Notes
              </Typography>
              {admissionOnlyRows.map((row, idx) => renderAdmissionAccordion(row, idx, "adm"))}
            </Box>
          )}

          {/* Discharge rows */}
          {dischargedRows.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="#059669" sx={{ mb: 1.5 }}>
                Discharge Records
              </Typography>
              {dischargedRows.map((row, idx) => renderDischargeAccordion(row, idx))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: LABORATORY
// ─────────────────────────────────────────────────────────────────────
const LabSection = ({ lab }) => {
  if (!lab || Object.keys(lab).length === 0)
    return <EmptySection message="No laboratory results found for this visit." />;
  return (
    <Box>
      {Object.entries(lab).map(([group, tests]) => (
        <Card key={group} elevation={0} sx={{ mb: 3, border: "1px solid #E2E8F0", borderRadius: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#7C3AED" }} />
              <Typography variant="subtitle2" fontWeight={700} color="#7C3AED">{group}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F5F3FF" }}>
                    {["Test", "Value", "Reference Range", "Flag", "Comments"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#4B5563" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tests.map((t, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t.name}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{t.value} {t.unit}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", fontStyle: "italic", color: "#94A3B8" }}>{t.refRange || "—"}</TableCell>
                      <TableCell>
                        {t.flag && (
                          <Chip label={t.flag} size="small" sx={{
                            fontSize: "0.68rem", fontWeight: 700,
                            bgcolor: t.flag === "HIGH" ? "#FEF2F2" : t.flag === "LOW" ? "#FFF7ED" : t.flag === "CRITICAL" ? "#7F1D1D" : "#F0FDF4",
                            color:  t.flag === "HIGH" ? "#DC2626" : t.flag === "LOW" ? "#D97706" : t.flag === "CRITICAL" ? "white"   : "#15803D",
                          }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", color: "#64748B" }}>{t.comments || t.otherComment || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 2, pt: 1.5, borderTop: "1px dashed #E2E8F0" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#F5F3FF", border: "1px solid #DDD6FE",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Person sx={{ fontSize: 15, color: "#7C3AED" }} />
              </Box>
              <Box>
                <Typography variant="caption" color="#94A3B8" fontWeight={600}
                  textTransform="uppercase" letterSpacing={0.5} sx={{ display: "block", lineHeight: 1 }}>
                  Technician
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#7C3AED">
                  {tests[0]?.technician || "—"} &nbsp;·&nbsp;
                  <Box component="span" sx={{ fontWeight: 400, color: "#94A3B8", fontSize: "0.8rem" }}>{tests[0]?.date}</Box>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: SCAN
// ─────────────────────────────────────────────────────────────────────
const ScanSection = ({ scan }) => {
  if (!scan || Object.keys(scan).length === 0)
    return <EmptySection message="No scan or imaging results found for this visit." />;
  return (
    <Box>
      {Object.entries(scan).map(([scanType, items]) => (
        <Card key={scanType} elevation={0} sx={{ mb: 3, border: "1px solid #E2E8F0", borderRadius: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#2563EB" }} />
              <Typography variant="subtitle2" fontWeight={700} color="#2563EB">{scanType}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#EFF6FF" }}>
                    {["Parameter", "Findings", "Flag", "Comments"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#4B5563" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", width: "20%" }}>{row.para}</TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", width: "55%" }}>{row.finding}</TableCell>
                      <TableCell sx={{ width: "10%" }}>
                        {row.flag && (
                          <Chip label={row.flag} size="small" sx={{
                            fontSize: "0.68rem", fontWeight: 700,
                            bgcolor: row.flag === "ABNORMAL" ? "#FEF2F2" : "#FFF7ED",
                            color:  row.flag === "ABNORMAL" ? "#DC2626"  : "#D97706",
                          }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", color: "#94A3B8", width: "15%" }}>—</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 2, pt: 1.5, borderTop: "1px dashed #E2E8F0" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#EFF6FF", border: "1px solid #BFDBFE",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Person sx={{ fontSize: 15, color: "#2563EB" }} />
              </Box>
              <Box>
                <Typography variant="caption" color="#94A3B8" fontWeight={600}
                  textTransform="uppercase" letterSpacing={0.5} sx={{ display: "block", lineHeight: 1 }}>
                  Technician
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#2563EB">
                  {items[0]?.technician || "—"} &nbsp;·&nbsp;
                  <Box component="span" sx={{ fontWeight: 400, color: "#94A3B8", fontSize: "0.8rem" }}>{items[0]?.date}</Box>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: MEDICATIONS
// ─────────────────────────────────────────────────────────────────────
const MedicationsSection = ({ medications }) => {
  if (!medications || medications.length === 0)
    return <EmptySection message="No medications prescribed for this visit." />;
  return (
    <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <TableContainer sx={{ maxHeight: medications.length > 6 ? 340 : "auto" }}>
          <Table stickyHeader={medications.length > 6} size="small">
            <TableHead>
              <TableRow>
                {["Date", "Medication", "Route", "Dose", "Frequency", "Duration", "Qty", "Prescribed By", "Served By"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", bgcolor: "#FFFBEB" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {medications.map((med, idx) => (
                <TableRow key={idx} hover sx={{ "&:nth-of-type(odd)": { bgcolor: "#FFFBEB44" } }}>
                  <TableCell sx={{ fontSize: "0.8rem", color: "#64748B" }}>{med.date}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{med.medication}</TableCell>
                  <TableCell sx={{ fontSize: "0.8rem" }}>{med.route}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{med.dose || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "0.8rem" }}>{med.frequency}</TableCell>
                  <TableCell sx={{ fontSize: "0.8rem" }}>{med.days}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{med.quantity}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem" }}>{med.doctor || med.prescribedBy}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem" }}>{med.servedBy || med.prescribedBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────────────────────────────────────────────
// SECTION: NURSE NOTES
// ─────────────────────────────────────────────────────────────────────
const NurseNotesSection = ({ nurseNotes }) => {
  if (!nurseNotes || nurseNotes.length === 0)
    return <EmptySection message="No nurse notes recorded for this visit." />;
  return (
    <Box>
      {nurseNotes.map((note, idx) => (
        <Card key={idx} elevation={0}
          sx={{ mb: 2, border: "1px solid #D1FAE5", borderRadius: 2,
            "&:hover": { boxShadow: "0 4px 12px #05966922", transition: "all 0.2s" } }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="#64748B" fontWeight={600}>{note.dat}</Typography>
                {note.time && (
                  <Typography variant="caption" color="#94A3B8" sx={{ display: "block" }}>{note.time}</Typography>
                )}
              </Box>
            </Box>
            <Typography variant="body2" color="#374151"
              sx={{ lineHeight: 1.7, bgcolor: "#F0FDF4", p: 1.5, borderRadius: 1, border: "1px solid #BBF7D0" }}>
              {note.note}
            </Typography>
            <DoctorBadge name={note.doctor} label="Nurse" color="#059669" bg="#ECFDF5" border="#A7F3D0" />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────
// DOCX BUILDER
// ─────────────────────────────────────────────────────────────────────
const buildDocxDocument = ({
  patientData, visitRow, isIPD,
  opdDetails, vitalsRows, ipdDiagnosisRows, ipdAdmissionRows, nurseNotes,
}) => {
  const visitDate     = visitRow?.dat || "";
  const dischargeDate = visitRow?.d_dat && visitRow.d_dat !== visitDate ? visitRow.d_dat : visitDate;
  const printedAt     = new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

  const W = 9026;
  const accent = isIPD ? "6D28D9" : "0F766E";

  const thinBorder = { style: BorderStyle.SINGLE, size: 1, color: "E2E8F0" };
  const allBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
  const cellPad    = { top: 60, bottom: 60, left: 120, right: 120 };
  const smallPad   = { top: 60, bottom: 60, left: 80,  right: 80  };

  const children = [];

  const p = (runs, opts = {}) =>
    new DocxParagraph({ children: Array.isArray(runs) ? runs : [runs], ...opts });
  const run = (text, opts = {}) =>
    new TextRun({ text: String(text ?? ""), font: "Arial", ...opts });

  const sectionHead = (text) => p(
    run(text, { bold: true, size: 22, color: accent }),
    { spacing: { before: 320, after: 120 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: accent, space: 4 } } }
  );
  const subHead = (text, color) => p(
    run(text, { bold: true, size: 20, color }),
    { spacing: { before: 160, after: 80 } }
  );
  const fieldLine = (label, value) => {
    const v = String(value ?? "").trim();
    if (!v || v === "null" || v === "0" || v === "?") return null;
    return p([
      run(`${label}: `, { bold: true, size: 20, color: "64748B" }),
      run(v, { size: 20, color: "1E293B" }),
    ], { spacing: { before: 60, after: 60 } });
  };
  const dateMeta = (dat, time, color = "64748B") => p(
    run(`${dat || "—"}${time ? `  ·  ${time}` : ""}`, { bold: true, size: 18, color }),
    { spacing: { before: 120, after: 60 } }
  );
  const doctorLine = (label, name, color) =>
    name && String(name).trim() && String(name).trim() !== "null"
      ? p(run(`${label}: ${name}`, { size: 18, italics: true, color }),
          { spacing: { before: 60, after: 100 } })
      : null;
  const push = (...items) => items.forEach(item => { if (item) children.push(item); });

  const cell = (text, width, opts = {}) => new DocxTableCell({
    borders: allBorders,
    width: { size: width, type: WidthType.DXA },
    margins: opts.smallPad ? smallPad : cellPad,
    shading: { fill: opts.fill || "FFFFFF", type: ShadingType.CLEAR },
    verticalAlign: opts.vAlign,
    children: [p(run(String(text ?? ""), { size: opts.size || 18, bold: opts.bold, color: opts.color, italics: opts.italics }), { alignment: opts.align })],
  });
  const headerCell = (text, width, fill) => new DocxTableCell({
    borders: allBorders,
    width: { size: width, type: WidthType.DXA },
    margins: smallPad,
    shading: { fill, type: ShadingType.CLEAR },
    children: [p(run(text, { size: 16, bold: true, color: "4B5563" }))],
  });

  // ── Document header ───────────────────────────────────────────────
  push(
    p(run(isIPD ? "Inpatient Admission Record" : "Outpatient Visit Record",
        { bold: true, size: 32, color: accent }), { spacing: { before: 0, after: 120 } }),
    p([
      run("Patient: ", { bold: true, size: 20 }), run(patientData?.fullName || "—", { size: 20 }),
      run("   ID: ",   { bold: true, size: 20 }), run(patientData?.id || "—",       { size: 20 }),
      run("   Visit: ",{ bold: true, size: 20 }),
      run(visitDate + (dischargeDate !== visitDate ? ` → ${dischargeDate}` : ""), { size: 20 }),
      ...(isIPD && visitRow?.dayCount
        ? [run(`   (${visitRow.dayCount} day${visitRow.dayCount !== 1 ? "s" : ""})`, { size: 20, color: "64748B" })]
        : []),
    ], { spacing: { before: 0, after: 60 } }),
    p(run(`Printed: ${printedAt}`, { size: 16, italics: true, color: "94A3B8" }),
      { spacing: { before: 0, after: 280 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 2, color: "E2E8F0", space: 4 } } }),
  );

  // ── OPD Notes (OPD visits only) ───────────────────────────────────
  if (!isIPD && opdDetails) {
    const rows = [
      ["Patient Complaint",    opdDetails.complaint],
      ["History",              opdDetails.history],
      ["Physical Examination", opdDetails.physicalExam],
      ["Other Documentation",  opdDetails.otherDocumentation],
      ["Diagnosis",            opdDetails.diagnosis],
      ["Procedure",            opdDetails.Procedure_],
    ].filter(([, v]) => v && String(v).trim() !== "" && String(v).trim() !== "null");
    if (rows.length) {
      push(sectionHead("DOCTOR'S NOTES"));
      rows.forEach(([l, v]) => push(fieldLine(l, v)));
      push(doctorLine("Attending Physician", opdDetails.doctor, "0D9488"));
    }
  }

  // ── Vitals ────────────────────────────────────────────────────────
  const vitalDefs = [
    { label: "Temperature",      key: "tem",   unit: "°C"   },
    { label: "Pulse",            key: "pulse", unit: "bpm"  },
    { label: "Blood Pressure",   key: "pre",   unit: "mmHg" },
    { label: "Respiratory Rate", key: "res",   unit: "/min" },
    { label: "Weight",           key: "we",    unit: "kg"   },
    { label: "Height",           key: "high",  unit: "cm"   },
    { label: "SpO2",             key: "spo2",  unit: "%"    },
    { label: "BMI",              key: "bmi",   unit: ""     },
  ];
  if (vitalsRows?.length) {
    const validRows = vitalsRows.filter(row =>
      vitalDefs.some(d => { const v = String(row[d.key] ?? "").trim(); return v && v !== "0" && v !== "?" && v !== "null"; })
    );
    if (validRows.length) {
      push(sectionHead("VITAL SIGNS"));
      validRows.forEach(row => {
        const valid = vitalDefs.filter(d => { const v = String(row[d.key] ?? "").trim(); return v && v !== "0" && v !== "?" && v !== "null"; });
        if (!valid.length) return;
        push(dateMeta(row.dat_vist, row.time));
        children.push(new DocxTable({
          width: { size: W, type: WidthType.DXA },
          columnWidths: [2800, 6226],
          rows: valid.map(d => new DocxTableRow({ children: [
            new DocxTableCell({ borders: allBorders, width: { size: 2800, type: WidthType.DXA }, margins: cellPad, shading: { fill: "F8FAFB", type: ShadingType.CLEAR }, children: [p(run(d.label, { bold: true, size: 18, color: "475569" }))] }),
            new DocxTableCell({ borders: allBorders, width: { size: 6226, type: WidthType.DXA }, margins: cellPad, children: [p(run(`${row[d.key]}${d.unit ? " " + d.unit : ""}`, { bold: true, size: 18 }))] }),
          ] })),
        }));
        push(doctorLine("Recorded By", row.doctor, "DC2626"));
      });
    }
  }

  // ── IPD sections ──────────────────────────────────────────────────
  if (isIPD && ipdDiagnosisRows?.length) {
    const diagFields = [
      { label: "Patient Complaint",    key: "symptorms"   },
      { label: "Examination Result",   key: "ex_result"   },
      { label: "Patient History",      key: "His_Comp"    },
      { label: "Physical Examination", key: "Other_doc"   },
      { label: "Diagnosis",            key: "documenting" },
      { label: "Procedure",            key: "Procedure_"  },
    ];

    // Split by admission date
    const preAdmRows = ipdDiagnosisRows.filter(r => sameDay(r.dat, visitDate));
    const wardRows   = ipdDiagnosisRows.filter(r => !sameDay(r.dat, visitDate));

    if (preAdmRows.some(r => diagFields.some(f => r[f.key]?.trim()))) {
      push(sectionHead("PRE-ADMISSION OUTPATIENT CONSULTATION NOTE"));
      preAdmRows.forEach(row => {
        const rf = diagFields.filter(f => { const v = String(row[f.key] ?? "").trim(); return v && v !== "null"; });
        if (!rf.length) return;
        push(dateMeta(row.dat, null, "BE185D"));
        rf.forEach(f => push(fieldLine(f.label, row[f.key])));
        push(doctorLine("Doctor", row.doctor, "BE185D"));
      });
    }

    if (wardRows.some(r => diagFields.some(f => r[f.key]?.trim()))) {
      push(sectionHead("ADMISSION INFORMATION"));
      push(subHead("WARD NOTES", "BE185D"));
      wardRows.forEach(row => {
        const rf = diagFields.filter(f => { const v = String(row[f.key] ?? "").trim(); return v && v !== "null"; });
        if (!rf.length) return;
        push(dateMeta(row.dat, null, "BE185D"));
        rf.forEach(f => push(fieldLine(f.label, row[f.key])));
        push(doctorLine("Doctor", row.doctor, "BE185D"));
      });
    }
  }

  // ── IPD Admission rows ────────────────────────────────────────────
  if (ipdAdmissionRows?.length) {
    const admFields = [
      { label: "Patient Complaint",      key: "symptorms"             },
      { label: "Examination Result",     key: "ex_result"             },
      { label: "Physical Examination",   key: "Other_doc"             },
      { label: "Surgical Notes",         key: "Surgical_Notes"        },
      { label: "Doctor's Plan",          key: "plan"                  },
      { label: "Discharge Note",         key: "discharge_note"        },
      { label: "Diagnosis",              key: "documenting"           },
      { label: "Procedure",              key: "Procedure_"            },
      { label: "Nurse Discharge Reason", key: "Nuse_discharge_reason" },
      { label: "Nurse Discharge Notes",  key: "Nuse_discharge_notes"  },
      { label: "Discharge Date/Time",    key: "Nuse_discharge_date"   },
    ];
    const norm = v => String(v || "").trim().toLowerCase();
    const progressRows  = ipdAdmissionRows.filter(r => norm(r.state) !== "discharged");
    const dischargeRows = ipdAdmissionRows.filter(r => norm(r.state) === "discharged");
    const hasAny = ipdAdmissionRows.some(r => admFields.some(f => r[f.key]?.trim()));

    if (hasAny) {
      // Only push the section header if we haven't already (from wardDiag above)
      const wardRowsExist = isIPD && ipdDiagnosisRows?.some(r => !sameDay(r.dat, visitDate));
      if (!wardRowsExist) push(sectionHead("ADMISSION INFORMATION"));

      if (progressRows.length) {
        push(subHead("ADMISSION / PROGRESS NOTES", "7C3AED"));
        progressRows.forEach(row => {
          const rf = admFields.filter(f => { const v = String(row[f.key] ?? "").trim(); return v && v !== "null" && v !== "0" && v !== "?"; });
          if (!rf.length) return;
          push(p([
            run(row.dat || "—", { bold: true, size: 18, color: "7C3AED" }),
            ...(row.note_time ? [run(`  ·  ${row.note_time}`, { size: 18, color: "94A3B8" })] : []),
            ...(row.state     ? [run(`  [${row.state}]`,      { size: 18, color: "5B21B6" })] : []),
          ], { spacing: { before: 120, after: 60 } }));
          rf.forEach(f => push(fieldLine(f.label, row[f.key])));
          push(doctorLine("Documented By", row.doctor, "7C3AED"));
        });
      }

      if (dischargeRows.length) {
        push(subHead("DISCHARGE RECORDS", "059669"));
        const discFields = [
          { label: "Patient Complaint",      key: "symptorms"             },
          { label: "Examination Result",     key: "ex_result"             },
          { label: "Discharge Note",         key: "discharge_note"        },
          { label: "Diagnosis",              key: "documenting"           },
          { label: "Procedure",              key: "Procedure_"            },
          { label: "Nurse Discharge Reason", key: "Nuse_discharge_reason" },
          { label: "Nurse Discharge Notes",  key: "Nuse_discharge_notes"  },
          { label: "Discharge Date/Time",    key: "Nuse_discharge_date"   },
        ];
        dischargeRows.forEach(row => {
          const rf = discFields.filter(f => { const v = String(row[f.key] ?? "").trim(); return v && v !== "null" && v !== "0" && v !== "?"; });
          if (!rf.length) return;
          push(p([
            run(row.dat || "—", { bold: true, size: 18, color: "059669" }),
            ...(row.note_time ? [run(`  ·  ${row.note_time}`, { size: 18, color: "94A3B8" })] : []),
            run("  [DISCHARGED]", { size: 18, color: "065F46", bold: true }),
          ], { spacing: { before: 120, after: 60 } }));
          rf.forEach(f => push(fieldLine(f.label, row[f.key])));
          push(doctorLine("Discharged By", row.doctor, "059669"));
        });
      }
    }
  }

  // ── Lab ───────────────────────────────────────────────────────────
  if (opdDetails?.lab && Object.keys(opdDetails.lab).length) {
    push(sectionHead("LABORATORY RESULTS"));
    const labCols = [2300, 1400, 2000, 900, 2426];
    Object.entries(opdDetails.lab).forEach(([group, tests]) => {
      push(subHead(group, "7C3AED"));
      children.push(new DocxTable({
        width: { size: W, type: WidthType.DXA }, columnWidths: labCols,
        rows: [
          new DocxTableRow({ children: ["Test","Value","Reference Range","Flag","Comments"].map((h, i) => headerCell(h, labCols[i], "F5F3FF")) }),
          ...tests.map(t => new DocxTableRow({ children: [
            cell(t.name,                             labCols[0], { bold: true }),
            cell(`${t.value || ""} ${t.unit || ""}`.trim(), labCols[1]),
            cell(t.refRange || "—",                  labCols[2], { italics: true, color: "94A3B8" }),
            cell(t.flag || "",                        labCols[3], { bold: true, color: t.flag === "HIGH" ? "DC2626" : t.flag === "LOW" ? "D97706" : t.flag === "CRITICAL" ? "7F1D1D" : "15803D" }),
            cell(t.comments || t.otherComment || "", labCols[4], { color: "64748B" }),
          ] })),
        ],
      }));
      push(doctorLine("Technician", tests[0]?.technician ? `${tests[0].technician}  ·  ${tests[0].date || ""}` : null, "7C3AED"));
    });
  }

  // ── Scan ──────────────────────────────────────────────────────────
  if (opdDetails?.scan && Object.keys(opdDetails.scan).length) {
    push(sectionHead("SCAN / IMAGING"));
    const scanCols = [1800, 5226, 1000, 1000];
    Object.entries(opdDetails.scan).forEach(([scanType, items]) => {
      push(subHead(scanType, "2563EB"));
      children.push(new DocxTable({
        width: { size: W, type: WidthType.DXA }, columnWidths: scanCols,
        rows: [
          new DocxTableRow({ children: ["Parameter","Findings","Flag","Comments"].map((h, i) => headerCell(h, scanCols[i], "EFF6FF")) }),
          ...items.map(r => new DocxTableRow({ children: [
            cell(r.para,      scanCols[0], { bold: true }),
            cell(r.finding,   scanCols[1]),
            cell(r.flag || "", scanCols[2], { bold: true, color: r.flag === "ABNORMAL" ? "DC2626" : "D97706" }),
            cell("—",         scanCols[3], { color: "94A3B8" }),
          ] })),
        ],
      }));
      push(doctorLine("Technician", items[0]?.technician ? `${items[0].technician}  ·  ${items[0].date || ""}` : null, "2563EB"));
    });
  }

  // ── Medications ───────────────────────────────────────────────────
  if (opdDetails?.medications?.length) {
    push(sectionHead("MEDICATIONS"));
    // 9 columns now (Dose added between Route and Frequency). Widths
    // rebalanced from the original 8-column layout (sum = 9026) so the
    // table still fills the page width; pulled width off Medication and
    // the Prescribed/Served columns to make room for a compact Dose
    // column.
    const medCols = [800, 1700, 700, 600, 1100, 700, 500, 1463, 1463];
    children.push(new DocxTable({
      width: { size: W, type: WidthType.DXA }, columnWidths: medCols,
      rows: [
        new DocxTableRow({ children: ["Date","Medication","Route","Dose","Frequency","Duration","Qty","Prescribed By","Served By"].map((h, i) => headerCell(h, medCols[i], "FFFBEB")) }),
        ...opdDetails.medications.map((m, i) => new DocxTableRow({ children: [
          cell(m.date,                             medCols[0], { fill: i%2===0?"FFFBEB":"FFFFFF", color: "64748B", smallPad: true, size: 14 }),
          cell(m.medication,                       medCols[1], { fill: i%2===0?"FFFBEB":"FFFFFF", bold: true,       smallPad: true, size: 14 }),
          cell(m.route,                            medCols[2], { fill: i%2===0?"FFFBEB":"FFFFFF",                   smallPad: true, size: 14 }),
          cell(String(m.dose ?? "") || "—",        medCols[3], { fill: i%2===0?"FFFBEB":"FFFFFF", bold: true, align: AlignmentType.CENTER, smallPad: true, size: 14 }),
          cell(m.frequency,                        medCols[4], { fill: i%2===0?"FFFBEB":"FFFFFF",                   smallPad: true, size: 14 }),
          cell(m.days,                             medCols[5], { fill: i%2===0?"FFFBEB":"FFFFFF",                   smallPad: true, size: 14 }),
          cell(m.quantity,                         medCols[6], { fill: i%2===0?"FFFBEB":"FFFFFF", bold: true, align: AlignmentType.CENTER, smallPad: true, size: 14 }),
          cell(m.doctor || m.prescribedBy || "",   medCols[7], { fill: i%2===0?"FFFBEB":"FFFFFF",                   smallPad: true, size: 14 }),
          cell(m.servedBy || m.prescribedBy || "", medCols[8], { fill: i%2===0?"FFFBEB":"FFFFFF",                   smallPad: true, size: 14 }),
        ] })),
      ],
    }));
  }

  // ── Nurse Notes ───────────────────────────────────────────────────
  if (nurseNotes?.length) {
    push(sectionHead("NURSE NOTES"));
    nurseNotes.forEach(note => {
      push(
        dateMeta(note.dat, note.time),
        p(run(note.note || "", { size: 20, color: "374151" }), { spacing: { before: 0, after: 60 }, indent: { left: 360 } }),
        doctorLine("Nurse", note.doctor, "059669"),
      );
    });
  }

  // ── Footer ────────────────────────────────────────────────────────
  push(p(
    run(`TotalCare Hospital Management System  ·  ${isIPD ? "Inpatient Admission Record" : "Outpatient Visit Record"}  ·  Printed ${printedAt}`,
      { size: 16, italics: true, color: "94A3B8" }),
    { spacing: { before: 480, after: 0 },
      border: { top: { style: BorderStyle.SINGLE, size: 2, color: "E2E8F0", space: 6 } } }
  ));

  return new DocxDocument({
    styles: { default: { document: { run: { font: "Arial", size: 20 } } } },
    sections: [{
      properties: { page: { size: { width: 11906, height: 16838 }, margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
      children,
    }],
  });
};


// ─────────────────────────────────────────────────────────────────────
// PRINT HELPER
// ─────────────────────────────────────────────────────────────────────
const buildPrintHTML = ({
  patientData, visitRow, isIPD,
  opdDetails, vitalsRows, ipdDiagnosisRows, ipdAdmissionRows, nurseNotes,
}) => {
  const visitDate     = visitRow?.dat || "";
  const dischargeDate = visitRow?.d_dat && visitRow.d_dat !== visitDate ? visitRow.d_dat : visitDate;
  const printedAt     = new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

  const esc = v => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const sectionHeader = (title, color) =>
    `<div class="section-header" style="border-left:4px solid ${color};padding-left:10px;margin:24px 0 12px">
       <h3 style="margin:0;color:${color};font-size:13px;text-transform:uppercase;letter-spacing:.06em">${esc(title)}</h3>
     </div>`;

  const fieldRow = (label, value) => {
    const v = String(value ?? "").trim();
    if (!v || v === "null" || v === "0" || v === "?") return "";
    return `<div class="field-row"><span class="field-label">${esc(label)}</span><span class="field-value">${esc(value)}</span></div>`;
  };

  const card = (content, borderColor = "#E2E8F0") =>
    `<div class="card" style="border-color:${borderColor}">${content}</div>`;

  const badge = (label, text, color) =>
    text && String(text).trim() && String(text).trim() !== "null"
      ? `<div class="badge" style="color:${color};border-color:${color}22;background:${color}0d"><span class="badge-label">${esc(label)}:</span> ${esc(text)}</div>`
      : "";

  const diagFields = [
    { label: "Patient Complaint",    key: "symptorms"   },
    { label: "Examination Result",   key: "ex_result"   },
    { label: "Patient History",      key: "His_Comp"    },
    { label: "Physical Examination", key: "Other_doc"   },
    { label: "Diagnosis",            key: "documenting" },
    { label: "Procedure",            key: "Procedure_"  },
  ];

  // ── OPD Notes (OPD only) ─────────────────────────────────────────
  let opdBlock = "";
  if (!isIPD && opdDetails) {
    const rows = [
      ["Patient Complaint",    opdDetails.complaint],
      ["History",              opdDetails.history],
      ["Physical Examination", opdDetails.physicalExam],
      ["Other Documentation",  opdDetails.otherDocumentation],
      ["Diagnosis",            opdDetails.diagnosis],
      ["Procedure",            opdDetails.Procedure_],
    ].filter(([, v]) => v && String(v).trim() !== "");
    if (rows.length) {
      opdBlock = sectionHeader("Doctor's Notes", "#0D9488") +
        card(rows.map(([l, v]) => fieldRow(l, v)).join("") + badge("Attending Physician", opdDetails.doctor, "#0D9488"), "#CCFBF1");
    }
  }

  // ── Vitals ───────────────────────────────────────────────────────
  const vitalDefs = [
    { label: "Temperature",      key: "tem",   unit: "°C"   },
    { label: "Pulse",            key: "pulse", unit: "bpm"  },
    { label: "Blood Pressure",   key: "pre",   unit: "mmHg" },
    { label: "Respiratory Rate", key: "res",   unit: "/min" },
    { label: "Weight",           key: "we",    unit: "kg"   },
    { label: "Height",           key: "high",  unit: "cm"   },
    { label: "SpO₂",             key: "spo2",  unit: "%"    },
    { label: "BMI",              key: "bmi",   unit: ""     },
  ];
  let vitalsBlock = "";
  if (vitalsRows?.length) {
    const rows = vitalsRows.map(row => {
      const cells = vitalDefs
        .filter(d => { const v = String(row[d.key] ?? "").trim(); return v && v !== "0" && v !== "?" && v !== "null"; })
        .map(d => `<tr><td class="vt-label">${esc(d.label)}</td><td class="vt-value">${esc(row[d.key])}${d.unit ? `<span class="vt-unit"> ${d.unit}</span>` : ""}</td></tr>`)
        .join("");
      if (!cells) return "";
      return card(
        `<div class="card-meta">📅 ${esc(row.dat_vist || "")}${row.time ? ` &nbsp;·&nbsp; ${esc(row.time)}` : ""}</div>
         <table class="vitals-table"><tbody>${cells}</tbody></table>` + badge("Recorded By", row.doctor, "#DC2626"),
        "#FECACA"
      );
    }).join("");
    if (rows.trim()) vitalsBlock = sectionHeader("Vital Signs", "#DC2626") + rows;
  }

  // ── IPD: split diagnosis rows ────────────────────────────────────
  const preAdmDiagRows = (ipdDiagnosisRows || []).filter(r => sameDay(r.dat, visitDate));
  const wardDiagRows   = (ipdDiagnosisRows || []).filter(r => !sameDay(r.dat, visitDate));

  // Pre-admission note
  let ipdPreAdmBlock = "";
  if (preAdmDiagRows.length > 0) {
    const rows = preAdmDiagRows.map(row => {
      const fields = diagFields.map(({ label, key }) => fieldRow(label, row[key])).join("");
      if (!fields.trim()) return "";
      return card(`<div class="card-meta">📅 ${esc(row.dat || "—")}</div>` + fields + badge("Doctor", row.doctor, "#BE185D"), "#FBCFE8");
    }).join("");
    if (rows.trim()) ipdPreAdmBlock = sectionHeader("Pre-Admission Outpatient Consultation Note", "#BE185D") + rows;
  }

  // Admission information (ward diag + admission rows)
  let ipdAdmBlock = "";
  {
    const normalizeState = v => String(v || "").trim().toLowerCase();

    const wardDiagBlock = wardDiagRows.length > 0
      ? `<div class="sub-header" style="color:#BE185D">Ward Notes</div>` +
        wardDiagRows.map(row => {
          const fields = diagFields.map(({ label, key }) => fieldRow(label, row[key])).join("");
          if (!fields.trim()) return "";
          return card(`<div class="card-meta">📅 ${esc(row.dat || "—")}</div>` + fields + badge("Doctor", row.doctor, "#BE185D"), "#FCE7F3");
        }).join("")
      : "";

    const admFields = [
      { label: "Patient Complaint",      key: "symptorms"             },
      { label: "Examination Result",     key: "ex_result"             },
      { label: "Physical Examination",   key: "Other_doc"             },
      { label: "Surgical Notes",         key: "Surgical_Notes"        },
      { label: "Doctor's Plan",          key: "plan"                  },
      { label: "Discharge Note",         key: "discharge_note"        },
      { label: "Diagnosis",              key: "documenting"           },
      { label: "Procedure",              key: "Procedure_"            },
      { label: "Nurse Discharge Reason", key: "Nuse_discharge_reason" },
      { label: "Nurse Discharge Notes",  key: "Nuse_discharge_notes"  },
      { label: "Discharge Date/Time",    key: "Nuse_discharge_date"   },
    ];

    const renderAdmRow = (row, borderColor, doctorColor, stateLabel) => {
      const fields = admFields.map(({ label, key }) => fieldRow(label, row[key])).join("");
      if (!fields.trim()) return "";
      const stateChip = row.state
        ? `<span class="state-chip" style="background:${normalizeState(row.state) === "discharged" ? "#D1FAE5" : "#EDE9FE"};color:${normalizeState(row.state) === "discharged" ? "#065F46" : "#5B21B6"}">${esc(row.state)}</span>`
        : "";
      return card(
        `<div class="card-meta">📅 ${esc(row.dat || "—")}${row.note_time ? ` &nbsp;·&nbsp; ${esc(row.note_time)}` : ""} ${stateChip}</div>` +
        fields + badge(stateLabel, row.doctor, doctorColor),
        borderColor
      );
    };

    const progressRows = (ipdAdmissionRows || [])
      .filter(r => normalizeState(r.state) !== "discharged")
      .map(r => renderAdmRow(r, "#DDD6FE", "#7C3AED", "Documented By"))
      .join("");
    const dischargeRows = (ipdAdmissionRows || [])
      .filter(r => normalizeState(r.state) === "discharged")
      .map(r => renderAdmRow(r, "#A7F3D0", "#059669", "Discharged By"))
      .join("");

    const combined =
      wardDiagBlock +
      (progressRows.trim() ? `<div class="sub-header" style="color:#7C3AED">Admission / Progress Notes</div>${progressRows}` : "") +
      (dischargeRows.trim() ? `<div class="sub-header" style="color:#059669">Discharge Records</div>${dischargeRows}` : "");

    if (combined.trim()) ipdAdmBlock = sectionHeader("Admission Information", "#7C3AED") + combined;
  }

  // ── Lab ──────────────────────────────────────────────────────────
  let labBlock = "";
  if (opdDetails?.lab && Object.keys(opdDetails.lab).length > 0) {
    const groups = Object.entries(opdDetails.lab).map(([group, tests]) => {
      const rows = tests.map(t => {
        const flagStyle = t.flag === "HIGH"     ? "color:#DC2626;background:#FEF2F2"
                        : t.flag === "LOW"      ? "color:#D97706;background:#FFF7ED"
                        : t.flag === "CRITICAL" ? "color:#fff;background:#7F1D1D"
                        : t.flag              ? "color:#15803D;background:#F0FDF4" : "";
        return `<tr>
          <td style="font-weight:600">${esc(t.name)}</td>
          <td>${esc(t.value)} ${esc(t.unit || "")}</td>
          <td style="font-style:italic;color:#94A3B8">${esc(t.refRange || "—")}</td>
          <td>${t.flag ? `<span class="flag-chip" style="${flagStyle}">${esc(t.flag)}</span>` : ""}</td>
          <td style="color:#64748B">${esc(t.comments || t.otherComment || "")}</td>
        </tr>`;
      }).join("");
      return card(
        `<div class="group-title" style="color:#7C3AED">${esc(group)}</div>
         <table class="results-table"><thead><tr><th>Test</th><th>Value</th><th>Reference Range</th><th>Flag</th><th>Comments</th></tr></thead><tbody>${rows}</tbody></table>` +
        badge("Technician", `${tests[0]?.technician || "—"} · ${tests[0]?.date || ""}`, "#7C3AED"),
        "#DDD6FE"
      );
    }).join("");
    if (groups.trim()) labBlock = sectionHeader("Laboratory Results", "#7C3AED") + groups;
  }

  // ── Scan ─────────────────────────────────────────────────────────
  let scanBlock = "";
  if (opdDetails?.scan && Object.keys(opdDetails.scan).length > 0) {
    const groups = Object.entries(opdDetails.scan).map(([scanType, items]) => {
      const rows = items.map(r => {
        const flagStyle = r.flag === "ABNORMAL" ? "color:#DC2626;background:#FEF2F2" : "color:#D97706;background:#FFF7ED";
        return `<tr>
          <td style="font-weight:600">${esc(r.para)}</td><td>${esc(r.finding)}</td>
          <td>${r.flag ? `<span class="flag-chip" style="${flagStyle}">${esc(r.flag)}</span>` : ""}</td>
          <td style="color:#94A3B8">—</td></tr>`;
      }).join("");
      return card(
        `<div class="group-title" style="color:#2563EB">${esc(scanType)}</div>
         <table class="results-table"><thead><tr><th>Parameter</th><th>Findings</th><th>Flag</th><th>Comments</th></tr></thead><tbody>${rows}</tbody></table>` +
        badge("Technician", `${items[0]?.technician || "—"} · ${items[0]?.date || ""}`, "#2563EB"),
        "#BFDBFE"
      );
    }).join("");
    if (groups.trim()) scanBlock = sectionHeader("Scan / Imaging", "#2563EB") + groups;
  }

  // ── Medications ──────────────────────────────────────────────────
  let medBlock = "";
  if (opdDetails?.medications?.length) {
    const rows = opdDetails.medications.map((m, i) =>
      `<tr style="${i%2===0?"background:#FFFBEB55":""}">
        <td style="color:#64748B">${esc(m.date)}</td>
        <td style="font-weight:600">${esc(m.medication)}</td>
        <td>${esc(m.route)}</td>
        <td style="text-align:center;font-weight:600">${esc(m.dose || "—")}</td>
        <td>${esc(m.frequency)}</td><td>${esc(m.days)}</td>
        <td style="text-align:center;font-weight:700">${esc(m.quantity)}</td>
        <td>${esc(m.doctor || m.prescribedBy || "")}</td>
        <td>${esc(m.servedBy || m.prescribedBy || "")}</td>
      </tr>`
    ).join("");
    medBlock = sectionHeader("Medications", "#D97706") +
      card(`<table class="results-table"><thead><tr><th>Date</th><th>Medication</th><th>Route</th><th>Dose</th><th>Frequency</th><th>Duration</th><th>Qty</th><th>Prescribed By</th><th>Served By</th></tr></thead><tbody>${rows}</tbody></table>`, "#FDE68A");
  }

  // ── Nurse Notes ──────────────────────────────────────────────────
  let nurseBlock = "";
  if (nurseNotes?.length) {
    const rows = nurseNotes.map(note =>
      card(
        `<div class="card-meta">📅 ${esc(note.dat || "")}${note.time ? ` &nbsp;·&nbsp; ${esc(note.time)}` : ""}</div>
         <div class="nurse-note">${esc(note.note)}</div>` + badge("Nurse", note.doctor, "#059669"),
        "#A7F3D0"
      )
    ).join("");
    nurseBlock = sectionHeader("Nurse Notes", "#059669") + rows;
  }

  const accentColor    = isIPD ? "#6D28D9" : "#0F766E";
  const visitTypeLabel = isIPD ? "Inpatient Admission Record" : "Outpatient Visit Record";
  const visitTypeBadge = isIPD ? "IPD" : "OPD";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Visit Record – ${esc(patientData?.fullName || "")} – ${esc(visitDate)}</title>
  <style>
    @media print {
      @page { size: A4; margin: 18mm 15mm; }
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #1e293b; background: #fff; line-height: 1.5; }
    .print-header { background: linear-gradient(135deg, ${accentColor}, ${accentColor}cc); color: #fff; padding: 18px 22px 14px; border-radius: 6px 6px 0 0; margin-bottom: 6px; }
    .print-header h1 { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
    .print-header .meta { font-size: 11px; opacity: .85; }
    .visit-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; background: rgba(255,255,255,.22); font-size: 10px; font-weight: 700; margin-left: 8px; vertical-align: middle; }
    .print-stamp { text-align: right; font-size: 10px; color: #94A3B8; margin-bottom: 14px; padding: 4px 0; border-bottom: 1px solid #E2E8F0; }
    .section-header { margin: 20px 0 10px; page-break-after: avoid; break-after: avoid; }
    .section-header h3 { font-size: 11px; font-weight: 700; }
    .sub-header { font-size: 11px; font-weight: 700; margin: 14px 0 6px; text-transform: uppercase; letter-spacing: .04em; }
    .card { border: 1px solid #E2E8F0; border-radius: 6px; padding: 14px 16px; margin-bottom: 10px; page-break-inside: avoid; }
    .card-meta { font-size: 10.5px; color: #64748B; font-weight: 600; margin-bottom: 10px; }
    .field-row { margin-bottom: 10px; }
    .field-label { display: block; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #64748B; margin-bottom: 2px; }
    .field-value { display: block; font-size: 11.5px; color: #334155; white-space: pre-wrap; }
    .badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 20px; border: 1px solid; font-size: 10.5px; font-weight: 600; margin-top: 10px; }
    .badge-label { font-weight: 400; opacity: .7; }
    .vitals-table { width: 100%; border-collapse: collapse; }
    .vitals-table td { padding: 4px 6px; font-size: 11px; }
    .vt-label { color: #475569; font-weight: 600; width: 45%; }
    .vt-value { font-weight: 700; }
    .vt-unit { color: #94A3B8; font-weight: 400; font-size: 10px; }
    .results-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
    .results-table th { background: #F8FAFC; font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; padding: 6px 8px; text-align: left; border-bottom: 1px solid #E2E8F0; color: #4B5563; }
    .results-table td { padding: 6px 8px; border-bottom: 1px solid #F1F5F9; vertical-align: top; }
    .results-table tr:last-child td { border-bottom: none; }
    .flag-chip, .state-chip { display: inline-block; padding: 1px 7px; border-radius: 10px; font-size: 9.5px; font-weight: 700; }
    .group-title { font-size: 12px; font-weight: 700; margin-bottom: 8px; }
    .nurse-note { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 4px; padding: 10px 12px; font-size: 11.5px; color: #374151; white-space: pre-wrap; }
    .print-footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #E2E8F0; font-size: 10px; color: #94A3B8; text-align: center; }
    .no-print { position: fixed; bottom: 24px; right: 24px; padding: 10px 22px; background: ${accentColor}; color: #fff; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px ${accentColor}66; }
    .no-print:hover { opacity: .9; }
  </style>
</head>
<body>
  <div class="print-header">
    <h1>${esc(visitTypeLabel)} <span class="visit-badge">${visitTypeBadge}</span></h1>
    <div class="meta">
      <strong>${esc(patientData?.fullName || "—")}</strong>
      &nbsp;·&nbsp; ID: ${esc(patientData?.id || "—")}
      &nbsp;·&nbsp; ${esc(visitDate)}${dischargeDate !== visitDate ? ` → ${esc(dischargeDate)}` : ""}
      ${isIPD && visitRow?.dayCount ? ` &nbsp;·&nbsp; ${visitRow.dayCount} day${visitRow.dayCount !== 1 ? "s" : ""}` : ""}
    </div>
  </div>
  <div class="print-stamp">Printed: ${printedAt}</div>
  ${opdBlock}
  ${vitalsBlock}
  ${ipdPreAdmBlock}
  ${ipdAdmBlock}
  ${labBlock}
  ${scanBlock}
  ${medBlock}
  ${nurseBlock}
  <div class="print-footer">TotalCare Hospital Management System &nbsp;·&nbsp; ${esc(visitTypeLabel)} &nbsp;·&nbsp; Printed ${printedAt}</div>
  <button class="no-print" onclick="window.print()">🖨 Print</button>
</body>
</html>`;
};


// ─────────────────────────────────────────────────────────────────────
// MAIN DIALOG
// ─────────────────────────────────────────────────────────────────────
const VisitRecordDialog = ({ open, onClose, patientData, visitRow }) => {
  const [activeSection,    setActiveSection]    = useState("opd");
  const [loading,          setLoading]          = useState(false);
  const [opdDetails,       setOpdDetails]       = useState(null);
  const [vitalsRows,       setVitalsRows]       = useState([]);
  const [ipdDiagnosisRows, setIpdDiagnosisRows] = useState([]);
  const [ipdAdmissionRows, setIpdAdmissionRows] = useState([]);
  const [nurseNotes,       setNurseNotes]       = useState([]);

  const lastLoadedKeyRef = useRef("");
  const loadingRef       = useRef(false);

  const isIPD         = visitRow?.isIPD || false;
  const visitDate     = visitRow?.dat   || "";
  const dischargeDate = visitRow?.d_dat && visitRow.d_dat !== visitDate ? visitRow.d_dat : visitDate;

  const sections = buildSections(isIPD);

  const loadVisitData = async () => {
    if (!patientData || !visitRow) return;
    const requestKey = [patientData.id, visitRow.dat, visitRow.d_dat || visitRow.dat, visitRow.isIPD ? "ipd" : "opd"].join("|");
    if (loadingRef.current) return;
    if (lastLoadedKeyRef.current === requestKey) return;
    loadingRef.current = true;
    lastLoadedKeyRef.current = requestKey;
    setOpdDetails(null); setVitalsRows([]); setIpdDiagnosisRows([]);
    setIpdAdmissionRows([]); setNurseNotes([]); setLoading(true);
    try {
      const baseParams = {
        patientId:     patientData.id,
        visitDate:     visitRow.dat,
        dischargeDate: visitRow.d_dat && visitRow.d_dat !== visitRow.dat ? visitRow.d_dat : visitRow.dat,
      };
      const [opdRes, vitalsRes, nurseRes] = await Promise.all([
        axios.get(getPatientVisitDetailsdb, { params: baseParams }),
        axios.get(visitVitalsdb,            { params: baseParams }),
        axios.get(visitNurseNotesdb,        { params: baseParams }),
      ]);
      if (opdRes.data.success)    setOpdDetails(opdRes.data.details);
      if (vitalsRes.data.success) setVitalsRows(vitalsRes.data.rows  || []);
      if (nurseRes.data.success)  setNurseNotes(nurseRes.data.notes  || []);

      const [ipdDiagRes, ipdAdmRes] = await Promise.all([
        axios.get(visitIPDDiagnosisdb, { params: baseParams }),
        axios.get(visitIPDAdmissiondb, { params: baseParams }),
      ]);
      if (ipdDiagRes.data.success) {
        const diagRows = ipdDiagRes.data.rows || [];
        const seen = new Set();
        setIpdDiagnosisRows(diagRows.filter(row => {
          const key = [row.trans_code, row.dat, row.note_time, row.documenting, row.doctor].join("|");
          if (seen.has(key)) return false;
          seen.add(key); return true;
        }));
      }
      if (ipdAdmRes.data.success) {
        const admRows = ipdAdmRes.data.rows || [];
        const seen = new Set();
        setIpdAdmissionRows(admRows.filter(row => {
          const key = [row.trans_code, row.dat, row.note_time, row.documenting, row.doctor, row.Nuse_discharge_date].join("|");
          if (seen.has(key)) return false;
          seen.add(key); return true;
        }));
      }
    } catch (err) {
      console.error("Error loading visit data:", err);
      lastLoadedKeyRef.current = "";
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (open && patientData && visitRow) {
      setActiveSection(isIPD ? "ipd" : "opd");
      loadVisitData();
    }
  }, [open, patientData?.id, visitRow?.dat, visitRow?.d_dat, visitRow?.isIPD]);

  useEffect(() => {
    if (!open) {
      loadingRef.current = false;
      lastLoadedKeyRef.current = "";
      setOpdDetails(null); setVitalsRows([]); setIpdDiagnosisRows([]);
      setIpdAdmissionRows([]); setNurseNotes([]);
    }
  }, [open]);

  const handlePrint = () => {
    const html = buildPrintHTML({ patientData, visitRow, isIPD, opdDetails, vitalsRows, ipdDiagnosisRows, ipdAdmissionRows, nurseNotes });
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) { alert("Pop-up blocked. Please allow pop-ups for this site and try again."); return; }
    win.document.open(); win.document.write(html); win.document.close();
    win.onload = () => win.print();
  };

  const handleDownloadDoc = async () => {
    try {
      const doc = buildDocxDocument({ patientData, visitRow, isIPD, opdDetails, vitalsRows, ipdDiagnosisRows, ipdAdmissionRows, nurseNotes });
      const blob = await Packer.toBlob(doc);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `Visit_Record_${(patientData?.fullName || "Patient").replace(/\s+/g, "_")}_${visitDate}.docx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating Word document:", err);
      alert("Failed to generate Word document. Please try again.");
    }
  };

  const activeSecDef = sections.find(s => s.key === activeSection);

  const renderContent = () => {
    if (loading) return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress sx={{ color: activeSecDef?.color }} />
      </Box>
    );
    switch (activeSection) {
      case "opd":         return <OpdNotesSection   details={opdDetails} />;
      case "vitals":      return <VitalsSection      vitalsRows={vitalsRows} />;
      case "ipd":         return <IpdSection
                                   diagnosisRows={ipdDiagnosisRows}
                                   admissionRows={ipdAdmissionRows}
                                   admissionDate={visitDate}          // ← key prop
                                 />;
      case "lab":         return <LabSection         lab={opdDetails?.lab} />;
      case "scan":        return <ScanSection        scan={opdDetails?.scan} />;
      case "medications": return <MedicationsSection medications={opdDetails?.medications} />;
      case "nurse":       return <NurseNotesSection  nurseNotes={nurseNotes} />;
      default:            return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: "92vh", maxHeight: "92vh", borderRadius: 3, overflow: "hidden" } }}>

      <DialogTitle sx={{ p: 0,
        background: isIPD ? "linear-gradient(135deg,#6D28D9,#7C3AED)" : "linear-gradient(135deg,#0F766E,#0D9488)",
        color: "white" }}>
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
              {isIPD ? <HotelRounded sx={{ fontSize: 22 }} /> : <LocalHospital sx={{ fontSize: 22 }} />}
              <Typography variant="h6" fontWeight={700}>
                {isIPD ? "Inpatient Admission Record" : "Outpatient Visit Record"}
              </Typography>
              <Chip label={isIPD ? "IPD" : "OPD"} size="small"
                sx={{ fontWeight: 700, bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: "0.75rem" }} />
            </Box>
            {patientData && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {patientData.fullName} &nbsp;·&nbsp; {patientData.id} &nbsp;·&nbsp; {visitDate}
                {dischargeDate !== visitDate ? ` → ${dischargeDate}` : ""}
                {isIPD && visitRow?.dayCount ? ` (${visitRow.dayCount} day${visitRow.dayCount !== 1 ? "s" : ""})` : ""}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white", mt: -0.5 }}><Close /></IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 0.5, px: 2, pb: 0, overflowX: "auto", scrollbarWidth: "none" }}>
          {sections.map(sec => (
            <Box key={sec.key} onClick={() => setActiveSection(sec.key)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                px: 2, py: 1, borderRadius: "8px 8px 0 0", cursor: "pointer",
                whiteSpace: "nowrap", fontSize: "0.82rem",
                fontWeight: activeSection === sec.key ? 700 : 500,
                bgcolor: activeSection === sec.key ? "white" : "rgba(255,255,255,0.12)",
                color: activeSection === sec.key ? (isIPD ? "#6D28D9" : "#0D9488") : "rgba(255,255,255,0.85)",
                transition: "all 0.18s",
                "&:hover": { bgcolor: activeSection === sec.key ? "white" : "rgba(255,255,255,0.22)" },
              }}>
              {sec.icon}{sec.label}
            </Box>
          ))}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", height: "100%", bgcolor: "#F8FAFB" }}>
        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: `${activeSecDef?.color}18`,
              display: "flex", alignItems: "center", justifyContent: "center", color: activeSecDef?.color }}>
              {activeSecDef?.icon}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#0F172A">{activeSecDef?.label}</Typography>
              <Typography variant="caption" color="#94A3B8">
                {patientData?.fullName} — {visitDate}
                {dischargeDate !== visitDate ? ` to ${dischargeDate}` : ""}
              </Typography>
            </Box>
          </Box>
          {renderContent()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "white", borderTop: "1px solid #E2E8F0" }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ textTransform: "none", borderColor: "#CBD5E1", color: "#64748B", borderRadius: 2 }}>
          Close
        </Button>
        <Button variant="outlined" onClick={handleDownloadDoc} disabled={loading}
          sx={{
            textTransform: "none", borderRadius: 2, fontWeight: 600,
            borderColor: isIPD ? "#7C3AED" : "#0D9488",
            color:       isIPD ? "#7C3AED" : "#0D9488",
            "&:hover": { bgcolor: isIPD ? "#F5F3FF" : "#F0FDFA" },
          }}>
          ⬇ Word (.docx)
        </Button>
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint} disabled={loading}
          sx={{
            textTransform: "none", borderRadius: 2, fontWeight: 600,
            background: isIPD ? "linear-gradient(135deg,#6D28D9,#7C3AED)" : "linear-gradient(135deg,#0F766E,#0D9488)",
          }}>
          Print Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitRecordDialog;